import { Fragment, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import { backendHOST } from '../../../client-config/client-config';
import { logout } from '../../../actions/auth';
import { PaymentMethods } from '../../../utils/enums/userTypes';
import { withRouter } from '../../hooks/withRouter';

const url = `${backendHOST}/api/invoice`;
const urlEnd = '/Invoice';

const urlAdmin = `${backendHOST}/api/scriptantiaAdmin`;

const emptyForm = {
  id_bizonylat: '',
  id_diak: '',
  datum: '',
  descr: '',
  osszeg: '',
  fizetett_orak_db: '',
  invoice_nr: '',
  fiz_mod: '',
  created_by_user: '',
  created_timestamp: '',
};

const FormManualInvoice = ({ auth }) => {
  const [invoices, setInvoices] = useState([]);
  const [students, setStudents] = useState([]);

  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.token,
  };

  const formatDateForInput = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString().slice(0, 10);
  };

  const formatDateTime = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString('hu-HU');
  };

  const numberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    return Number(value);
  };

  const canModifyInvoice = (invoice) => {
    return Number(invoice?.invoiced || 0) === 0;
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [invoiceResponse, studentsResponse] = await Promise.all([
        fetch(`${url}${urlEnd}`, {
          headers: authHeaders,
        }),
        fetch(`${urlAdmin}/Student?uType=D`, {
          headers: authHeaders,
        }),
      ]);

      if (!invoiceResponse.ok) {
        throw new Error('Nem sikerült betölteni a kézi számlákat.');
      }

      if (!studentsResponse.ok) {
        throw new Error('Nem sikerült betölteni a diákokat.');
      }

      const invoiceData = await invoiceResponse.json();
      const studentsData = await studentsResponse.json();

      setInvoices(Array.isArray(invoiceData) ? invoiceData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : []);
    } catch (err) {
      setError(err.message || 'Ismeretlen hiba történt betöltés közben.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStudentLabel = (studentId) => {
    const item = students.find(
      (student) => String(student.id) === String(studentId),
    );

    return item?.text || studentId || '';
  };

  const getPaymentMethodLabel = (paymentMethod) => {
    const item = PaymentMethods.find(
      (method) => String(method.key) === String(paymentMethod),
    );

    return item?.name || paymentMethod || '';
  };

  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const dateA = a.datum ? new Date(a.datum).getTime() : 0;
      const dateB = b.datum ? new Date(b.datum).getTime() : 0;

      return dateB - dateA;
    });
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return sortedInvoices;
    }

    return sortedInvoices.filter((invoice) =>
      [
        invoice.id_bizonylat,
        invoice.id_diak,
        getStudentLabel(invoice.id_diak),
        invoice.datum,
        invoice.descr,
        invoice.osszeg,
        invoice.fizetett_orak_db,
        invoice.invoiced,
        invoice.invoice_nr,
        invoice.fiz_mod,
        getPaymentMethodLabel(invoice.fiz_mod),
        invoice.created_by_user,
        invoice.created_timestamp,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        ),
    );
  }, [sortedInvoices, searchText, students]);

  const handleCreateClick = () => {
    setEditingInvoice(null);

    setFormData({
      ...emptyForm,
      datum: formatDateForInput(new Date()),
      created_by_user: auth.user.ext_id_user,
      fiz_mod: PaymentMethods?.[0]?.key || '',
    });

    setDialogOpen(true);
  };

  const handleEditClick = (invoice) => {
    if (!canModifyInvoice(invoice)) return;

    setEditingInvoice(invoice);

    setFormData({
      id_bizonylat: invoice.id_bizonylat ?? '',
      id_diak:
        invoice.id_diak === null || invoice.id_diak === undefined
          ? ''
          : String(invoice.id_diak),
      datum: formatDateForInput(invoice.datum),
      descr: invoice.descr ?? '',
      osszeg:
        invoice.osszeg === null || invoice.osszeg === undefined
          ? ''
          : String(invoice.osszeg),
      fizetett_orak_db:
        invoice.fizetett_orak_db === null ||
        invoice.fizetett_orak_db === undefined
          ? ''
          : String(invoice.fizetett_orak_db),
      invoice_nr: invoice.invoice_nr ?? '',
      fiz_mod: invoice.fiz_mod ?? '',
      created_by_user: invoice.created_by_user ?? '',
      created_timestamp: invoice.created_timestamp ?? '',
    });

    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (saving) return;

    setDialogOpen(false);
    setEditingInvoice(null);
    setFormData(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const normalizePayload = (data, isEdit) => {
    const payload = {
      id_diak: numberOrNull(data.id_diak),
      datum: data.datum || null,
      descr: data.descr || '',
      osszeg: numberOrNull(data.osszeg),
      fizetett_orak_db: numberOrNull(data.fizetett_orak_db),
      invoice_nr: data.invoice_nr || '',
      fiz_mod: data.fiz_mod || null,
      created_by_user: data.created_by_user || auth.user.ext_id_user,
    };

    if (isEdit) {
      payload.id_bizonylat = data.id_bizonylat;
    }

    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const isEdit = Boolean(editingInvoice);
      const payload = normalizePayload(formData, isEdit);

      const response = await fetch(`${url}${urlEnd}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          isEdit
            ? 'Nem sikerült módosítani a kézi számlát.'
            : 'Nem sikerült létrehozni a kézi számlát.',
        );
      }

      setMessage(isEdit ? 'Kézi számla módosítva.' : 'Kézi számla létrehozva.');

      setDialogOpen(false);
      setEditingInvoice(null);
      setFormData(emptyForm);

      await loadData();
    } catch (err) {
      setError(err.message || 'Mentés közben hiba történt.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (invoice) => {
    if (!canModifyInvoice(invoice)) return;

    const confirmed = window.confirm(
      `Biztosan törlöd ezt a kézi számlát?\n\n${getStudentLabel(
        invoice.id_diak,
      )}\n${formatDateForInput(invoice.datum)} - ${invoice.osszeg} Ft`,
    );

    if (!confirmed) return;

    setDeletingId(invoice.id_bizonylat);
    setError(null);

    try {
      const response = await fetch(`${url}${urlEnd}`, {
        method: 'DELETE',
        headers: authHeaders,
        body: JSON.stringify(invoice.id_bizonylat),
      });

      if (!response.ok) {
        throw new Error('Nem sikerült törölni a kézi számlát.');
      }

      setMessage('Kézi számla törölve.');

      await loadData();
    } catch (err) {
      setError(err.message || 'Törlés közben hiba történt.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent='space-between'
                spacing={2}
              >
                <Box>
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    Kézi számlázás
                  </Typography>

                  <Typography variant='body2' color='text.secondary'>
                    Kézi számlatételek rögzítése, módosítása és törlése.
                  </Typography>
                </Box>

                <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={handleCreateClick}
                >
                  Új kézi számla
                </Button>
              </Stack>

              <TextField
                label='Keresés'
                size='small'
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                sx={{ maxWidth: 320 }}
              />

              {error && (
                <Alert severity='error' onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant='outlined'>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Diák</TableCell>
                        <TableCell>Dátum</TableCell>
                        <TableCell>Tétel</TableCell>
                        <TableCell align='right'>Összeg</TableCell>
                        <TableCell>Számlaszám</TableCell>
                        <TableCell>Fizetési mód</TableCell>
                        <TableCell>Számlázva</TableCell>
                        <TableCell align='right'>Művelet</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredInvoices.map((invoice) => {
                        const canModify = canModifyInvoice(invoice);
                        const rowDeleting = deletingId === invoice.id_bizonylat;

                        return (
                          <TableRow key={invoice.id_bizonylat} hover>
                            <TableCell>{invoice.id_bizonylat}</TableCell>

                            <TableCell>
                              {getStudentLabel(invoice.id_diak)}
                            </TableCell>

                            <TableCell>
                              {formatDateForInput(invoice.datum) || '-'}
                            </TableCell>

                            <TableCell>{invoice.descr}</TableCell>

                            <TableCell align='right'>
                              {invoice.osszeg}
                            </TableCell>

                            <TableCell>{invoice.invoice_nr}</TableCell>

                            <TableCell>
                              {getPaymentMethodLabel(invoice.fiz_mod)}
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={canModify ? 'Nem' : 'Igen'}
                                size='small'
                                color={canModify ? 'warning' : 'success'}
                                variant='outlined'
                              />
                            </TableCell>

                            <TableCell align='right'>
                              <Tooltip
                                title={
                                  canModify
                                    ? 'Szerkesztés'
                                    : 'Számlázott tétel nem módosítható'
                                }
                              >
                                <span>
                                  <IconButton
                                    size='small'
                                    disabled={!canModify || rowDeleting}
                                    onClick={() => handleEditClick(invoice)}
                                  >
                                    <EditIcon fontSize='small' />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              <Tooltip
                                title={
                                  canModify
                                    ? 'Törlés'
                                    : 'Számlázott tétel nem törölhető'
                                }
                              >
                                <span>
                                  <IconButton
                                    size='small'
                                    color='error'
                                    disabled={!canModify || rowDeleting}
                                    onClick={() => handleDelete(invoice)}
                                  >
                                    <DeleteIcon fontSize='small' />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {filteredInvoices.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align='center'>
                            Nincs megjeleníthető kézi számla.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>
          {editingInvoice ? 'Kézi számla szerkesztése' : 'Új kézi számla'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 700 }}>
                Számlázandó tétel
              </Typography>

              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id='manual-invoice-student-label'>
                      Diák
                    </InputLabel>
                    <Select
                      labelId='manual-invoice-student-label'
                      label='Diák'
                      value={formData.id_diak}
                      onChange={(event) =>
                        handleFormChange('id_diak', event.target.value)
                      }
                    >
                      {students.map((student) => (
                        <MenuItem key={student.id} value={String(student.id)}>
                          {student.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label='Dátum'
                    type='date'
                    value={formData.datum}
                    onChange={(event) =>
                      handleFormChange('datum', event.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />

                  <FormControl fullWidth>
                    <InputLabel id='manual-invoice-payment-label'>
                      Fizetési mód
                    </InputLabel>
                    <Select
                      labelId='manual-invoice-payment-label'
                      label='Fizetési mód'
                      value={formData.fiz_mod}
                      onChange={(event) =>
                        handleFormChange('fiz_mod', event.target.value)
                      }
                    >
                      {PaymentMethods.map((method) => (
                        <MenuItem key={method.key} value={String(method.key)}>
                          {method.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label='Óraszám'
                    type='number'
                    value={formData.fizetett_orak_db}
                    onChange={(event) =>
                      handleFormChange('fizetett_orak_db', event.target.value)
                    }
                    fullWidth
                  />

                  <TextField
                    label='Összeg'
                    type='number'
                    value={formData.osszeg}
                    onChange={(event) =>
                      handleFormChange('osszeg', event.target.value)
                    }
                    fullWidth
                  />
                </Stack>

                <TextField
                  label='Tétel'
                  value={formData.descr}
                  onChange={(event) =>
                    handleFormChange('descr', event.target.value)
                  }
                  multiline
                  minRows={3}
                  fullWidth
                />

                <TextField
                  label='Számlaszám'
                  value={formData.invoice_nr}
                  onChange={(event) =>
                    handleFormChange('invoice_nr', event.target.value)
                  }
                  fullWidth
                />
              </Stack>
            </Box>

            {editingInvoice && (
              <Box>
                <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 700 }}>
                  Technikai adatok
                </Typography>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label='ID'
                    value={formData.id_bizonylat}
                    disabled
                    fullWidth
                  />

                  <TextField
                    label='Létrehozta'
                    value={formData.created_by_user}
                    disabled
                    fullWidth
                  />

                  <TextField
                    label='Létrehozás dátuma'
                    value={formatDateTime(formData.created_timestamp)}
                    disabled
                    fullWidth
                  />
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>
            Mégsem
          </Button>

          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? 'Mentés...' : 'Mentés'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Fragment>
  );
};

FormManualInvoice.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout })(
  withRouter(FormManualInvoice),
);
